import React from 'react'
import { Card, Form } from 'react-bootstrap'
import FiltersGrid from '../../_filters-grid/filters-grid'
import { PublisherOrCampaign } from '../../home/dashboard-content/types'
import { IPublisher } from '../types'
import DomainSettings from './domain-settings'
import DuplicateCampaignTable from './duplicate-campaigns-table'
import { ScriptOptionSets, ScriptSettingsState, isSameItem } from './script-settings-model'

type Props = {
    publishers: IPublisher[]
    options: ScriptOptionSets
    value: ScriptSettingsState
    onChange(nextValue: ScriptSettingsState): void
}

const ScriptSettingsEditor: React.FC<Props> = ({ publishers, options, value, onChange }) => {
    const handleSelectAll = (
        key: keyof ScriptSettingsState['filters'],
        nextOptions: string[] | PublisherOrCampaign[]
    ) => {
        onChange({
            ...value,
            filters: {
                ...value.filters,
                [key]: value.filters[key].length === nextOptions.length ? [] : [...nextOptions] as any,
            },
        })
    }

    const handleItemSelect = (
        type: keyof ScriptSettingsState['filters'],
        item: string | PublisherOrCampaign
    ) => {
        onChange({
            ...value,
            filters: {
                ...value.filters,
                [type]: (() => {
                    const current = value.filters[type] as Array<string | PublisherOrCampaign>
                    const exists = current.some(existing =>
                        typeof item === 'string' ? existing === item : isSameItem(existing, item)
                    )

                    return exists
                        ? current.filter(existing =>
                            typeof item === 'string' ? existing !== item : !isSameItem(existing, item)
                        )
                        : [...current, item]
                })() as any,
            },
        })
    }

    return (
        <>
            <Card className="shadow-sm border-0 mb-4">
                <Card.Body className="px-4 pt-3 pb-3">
                    <DomainSettings
                        allowedDomains={value.allowedDomains}
                        disallowedDomainRules={value.disallowedDomainRules}
                        onChange={(allowedDomains, disallowedDomainRules) =>
                            onChange({ ...value, allowedDomains, disallowedDomainRules })
                        }
                    />

                    <Form.Check
                        type="switch"
                        id="track-existing-affiliate-links"
                        className="mt-4"
                        label="Track existing affiliate links"
                        checked={value.trackExistingAffiliateLinks}
                        onChange={(event) => onChange({
                            ...value,
                            trackExistingAffiliateLinks: event.target.checked,
                        })}
                    />
                    <div className="text-muted small mt-2">
                        If a normal campaign match is not found, the script will try to recognize existing affiliate links and only replace the click reference.
                    </div>
                </Card.Body>
            </Card>

            <Card className="shadow-sm border-0 mb-4">
                <Card.Header className="bg-white border-0 pb-0 pt-4 px-4">
                    <h5 className="mb-0">Campaign Filters</h5>
                </Card.Header>
                <Card.Body className="px-4 pt-3 pb-4">
                    <FiltersGrid
                        opts={{
                            campaigns: options.campaigns,
                            countries: options.countries,
                            networks: options.networks,
                            publishers: [],
                            channels: options.channels,
                        }}
                        selectedFilters={value.filters}
                        allCollapsed
                        showSelectedCountInHeader
                        onSelectAll={handleSelectAll}
                        onItemSelect={handleItemSelect}
                    />

                    <DuplicateCampaignTable
                        publishers={publishers}
                        filters={value.filters}
                        initialDeselected={value.deselected}
                        onChange={(deselected) => onChange({ ...value, deselected })}
                    />
                </Card.Body>
            </Card>
        </>
    )
}

export default ScriptSettingsEditor